/**
 * Attribution helper — reads ?ref=<share_id>&src=<friend|peer|nomination>
 * from the URL on any page load and persists it in sessionStorage so
 * downstream API calls can attach it to their request body.
 *
 * Why sessionStorage (not localStorage): attribution is tab-scoped — if the
 * user reopens the site later without the ref, we shouldn't mis-credit a
 * share. Window close = attribution cleared. Matches the intent of "I came
 * here from this share" within a single visit.
 *
 * The `POST /api/track/share-land` call is fire-and-forget — it records the
 * click on the share document and emits `share.clicked` to activity_stream.
 * Only fires once per session per ref.
 */
const STORAGE_KEY = "laguna.attribution";
const LANDED_KEY = "laguna.attribution.landed";

const ALLOWED_SOURCES = new Set(["friend", "peer", "nomination"]);

function safeGet() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function safeSet(obj) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch {
        /* storage unavailable — degrade silently */
    }
}

/**
 * Read ref/src from the current URL (if present) and persist.
 * Returns the current attribution (possibly updated) or null.
 */
export function captureFromUrl() {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim();
    const src = (params.get("src") || "").trim();
    if (ref && ALLOWED_SOURCES.has(src)) {
        const stamp = { ref, src, capturedAt: new Date().toISOString() };
        safeSet(stamp);
        return stamp;
    }
    return safeGet();
}

export function currentAttribution() {
    return safeGet();
}

/**
 * Attach attribution (if any) to an outgoing request body. No-op when
 * sessionStorage is empty.
 */
export function withAttribution(body = {}) {
    const a = safeGet();
    if (!a) return body;
    return { ...body, ref: a.ref, src: a.src };
}

/**
 * Fire the backend landing tracker once per session per ref. Returns the
 * resolved attribution on success so the caller can trigger analytics.
 */
export async function trackLandingOnce(api, landingPath) {
    const a = safeGet();
    if (!a) return null;
    const landedKey = `${LANDED_KEY}:${a.ref}:${a.src}`;
    try {
        if (sessionStorage.getItem(landedKey)) return a;
        sessionStorage.setItem(landedKey, "1");
    } catch {
        /* ignore */
    }
    try {
        await api.post("/track/share-land", {
            ref: a.ref,
            src: a.src,
            landing_path: landingPath,
        });
    } catch {
        /* best-effort */
    }
    return a;
}

export function clearAttribution() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}
